// PlayersHub — admin Players hub.
//
// Sub-tab structure (with a live pending-count badge on Requests):
//
//   /people/players                → redirect → /people/players/requests
//   /people/players/requests       → PlayerRequestsPanel  (QR + queues)
//   /people/players/list           → PlayersListPanel     (roster)
//
// 2026-05-16 (Routing Restructure): the `bulk` and `referrals` sub-
// tabs moved to the Offers hub (`/offers/bulk-pay`, `/offers/
// referrals`) — both are payouts to players, so they belong with
// Offers + Claims. PlayersHub now mirrors AgentsHub's two-tab shape
// (requests + roster).
//
// Detail / workflow routes (/people/players/:id, /people/players/
// new) remain sibling pages without these sub-tabs (registered in
// App.tsx).
//
// MagentaTabBar with a numeric count prop powers the Requests badge.
// The badge polls every 30s — same cadence as the agent SPA's PlayersHub.

import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { motion } from 'motion/react';
import Icon from '@baaligames/shared-ui/components/Icon';
import MagentaTabBar from '@baaligames/shared-ui/admin/components/MagentaTabBar';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import PeopleSegmentBar from '../components/PeopleSegmentBar';

// 2026-05-20 — 30_000 → 60_000 to match the new bottom-nav rhythm.
const COUNT_POLL_MS = 60_000;

export default function PlayersHub() {
  const loc = useLocation();

  // Live count of pending player recharge + withdrawal requests. Drives
  // the magenta badge on the "Requests" sub-tab.
  const [pending, setPending] = useState<{ recharge: number; withdrawal: number }>({ recharge: 0, withdrawal: 0 });
  useEffect(() => {
    let alive = true;
    const fetchCounts = async () => {
      try {
        const [r, w] = await Promise.all([
          adminApi.get<{ total: number }>('/admin/payment-requests/recharge?state=pending&limit=1&offset=0'),
          adminApi.get<{ total: number }>('/admin/payment-requests/withdrawal?state=pending&limit=1&offset=0'),
        ]);
        if (alive) setPending({ recharge: r.total || 0, withdrawal: w.total || 0 });
      } catch { /* keep last good value */ }
    };
    fetchCounts();
    // 2026-05-20 — Skip polls when the tab is hidden.
    const iv = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      void fetchCounts();
    }, COUNT_POLL_MS);
    // Refresh on every nav inside this hub too — after an approve /
    // reject the count drops by one and we want the badge to follow.
    return () => { alive = false; clearInterval(iv); };
  }, [loc.pathname]);
  const totalPending = pending.recharge + pending.withdrawal;

  return (
    <>
    <PeopleSegmentBar current="players" />
    <div className="max-w-screen-2xl mx-auto px-2 sm:px-3 lg:px-6 py-3 lg:py-4 space-y-4">
      {/* Header — sky accent so the page colour matches the People
          section bubble in the admin bottom nav. */}
      <motion.header
        initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
        className="px-2 lg:px-4"
      >
        <div className="flex items-center gap-3">
          <span className="grid place-items-center w-10 h-10 rounded-xl
                           bg-sky-50 text-sky-700 ring-1 ring-sky-100
                           dark:bg-sky-400/15 dark:text-sky-300 dark:ring-sky-400/30">
            <Icon name="group" size={22} filled />
          </span>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl sm:text-2xl font-display font-semibold text-arena-900 dark:text-white tracking-tight">
              Players
            </h1>
            <p className="text-[12.5px] text-arena-600 dark:text-neon-200/70 truncate">
              Requests · roster — every player surface in one place
            </p>
          </div>
        </div>
      </motion.header>

      {/* Sub-tabs — sky accent matches the People section. The Requests
          tab carries the live pending-count badge so the admin sees the
          queue depth at a glance from any other sub-tab. */}
      <div className="px-2 lg:px-4">
        <MagentaTabBar
          size="lg"
          accent="sky"
          items={[
            { to: '/people/players/requests',  label: 'Requests',  icon: 'inbox',         matchPrefix: '/people/players/requests',  count: totalPending },
            { to: '/people/players/list',      label: 'Players',   icon: 'group',         matchPrefix: '/people/players/list' },
          ]}
        />
      </div>

      {/* Active sub-tab */}
      <div className="px-2 lg:px-4">
        <Outlet />
      </div>
    </div>
    </>
  );
}
